import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { depositarChequeFisico } from "./ApiServices";
import { formatMoneda } from "../pago_proveedores/utils";
import {
  TIPO_DEPOSITO,
  esCasaCentral,
  idInstrumentoFisico,
  idSucursalDe,
  tiposDepositoPermitidos,
} from "./chequesFisicos.constants";

export default function ModalDepositar({ open, onClose, cheque, sucursal, onOk }) {
  const inputRef = useRef(null);
  const [tipoDeposito, setTipoDeposito] = useState(TIPO_DEPOSITO.BANCO);
  const [banco, setBanco] = useState("");
  const [recaudadora, setRecaudadora] = useState("");
  const [obs, setObs] = useState("");
  const [foto, setFoto] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const tiposPermitidos = useMemo(
    () => tiposDepositoPermitidos(cheque, sucursal),
    [cheque, sucursal]
  );

  const soloBanco = tiposPermitidos.length === 1;
  const puedeRecaudadora = tiposPermitidos.includes(TIPO_DEPOSITO.RECAUDADORA);
  const esCc =
    esCasaCentral(idSucursalDe(sucursal)) ||
    esCasaCentral(cheque?.id_sucursal ?? cheque?.sucursal);

  useEffect(() => {
    if (!open) return;
    const inicial = tiposPermitidos[0] || TIPO_DEPOSITO.BANCO;
    setTipoDeposito(inicial);
    setBanco(cheque?.banco_emisor || "");
    setRecaudadora("");
    setObs("");
    setFoto(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  }, [open, cheque, tiposPermitidos]);

  const guardar = async () => {
    const id = idInstrumentoFisico(cheque);
    if (!id) return;
    setGuardando(true);
    setError("");
    try {
      await depositarChequeFisico(id, {
        tipo_deposito: tipoDeposito,
        banco:
          tipoDeposito === TIPO_DEPOSITO.BANCO
            ? banco.trim() || undefined
            : undefined,
        recaudadora:
          tipoDeposito === TIPO_DEPOSITO.RECAUDADORA
            ? recaudadora.trim() || undefined
            : undefined,
        obs: obs.trim() || undefined,
        foto,
      });
      onOk?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Error al depositar el cheque");
    } finally {
      setGuardando(false);
    }
  };

  const textoCustodia =
    tipoDeposito === TIPO_DEPOSITO.RECAUDADORA
      ? "entregado en recaudadora"
      : "depositado en banco";

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Depositar — N° {cheque?.nro_cheque || idInstrumentoFisico(cheque) || ""}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {esCc ? (
            <Alert severity="info">
              Casa Central solo puede depositar cheques en banco.
            </Alert>
          ) : null}
          {!esCc && soloBanco ? (
            <Alert severity="info">
              Esta sucursal no tiene recaudadora; el depósito es solo en banco.
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            El cheque pasa a <strong>Depositado</strong> y la custodia queda{" "}
            <strong>{textoCustodia}</strong>. Importe:{" "}
            {cheque?.importe != null ? formatMoneda(cheque.importe) : "—"}.
          </Typography>
          {puedeRecaudadora ? (
            <TextField
              select
              label="Tipo de depósito"
              size="small"
              fullWidth
              value={tipoDeposito}
              onChange={(e) => setTipoDeposito(e.target.value)}
              disabled={guardando || soloBanco}
            >
              <MenuItem value={TIPO_DEPOSITO.BANCO}>Depositar en banco</MenuItem>
              <MenuItem value={TIPO_DEPOSITO.RECAUDADORA}>
                Entregar en recaudadora
              </MenuItem>
            </TextField>
          ) : null}
          {tipoDeposito === TIPO_DEPOSITO.BANCO ? (
            <TextField
              label="Banco (opcional)"
              size="small"
              fullWidth
              value={banco}
              onChange={(e) => setBanco(e.target.value)}
              disabled={guardando}
              placeholder={cheque?.banco_emisor || ""}
            />
          ) : null}
          {tipoDeposito === TIPO_DEPOSITO.RECAUDADORA ? (
            <TextField
              label="Recaudadora (opcional)"
              size="small"
              fullWidth
              value={recaudadora}
              onChange={(e) => setRecaudadora(e.target.value)}
              disabled={guardando}
            />
          ) : null}
          <TextField
            label="Observaciones (opcional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            disabled={guardando}
          />
          <Stack spacing={0.5}>
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={guardando}
            >
              {foto ? "Cambiar foto" : "Adjuntar foto (opcional)"}
              <input
                ref={inputRef}
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => setFoto(e.target.files?.[0] || null)}
              />
            </Button>
            {foto ? (
              <Typography variant="caption" color="text.secondary">
                {foto.name}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={guardar}
          disabled={guardando}
          startIcon={
            guardando ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Depositar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
